/**
 * useEntityValueSearch.ts - Typo-tolerant lookup of filter values
 *
 * Wraps POST /groups/elastic/entity-values/search. Distinct from
 * fetchFilterSuggestions' "top values for the whole circle" facets: this finds a
 * specific, possibly rare value by name as the user types, across
 * English/Arabic/Sorani.
 */

import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useApi } from 'core';
import { FilterOption } from '@/types/filter.types';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const DEFAULT_LIMIT = 50;

// Stable identity so callers' useMemo deps don't thrash between renders.
const EMPTY: FilterOption[] = [];

interface EntityValueSearchHit {
    label?: string;
    value: string;
    count?: number;
    displayLabel?: string;
}

// entity_values holds one document per (circleId, field, value) - the doc _id is a
// hash of exactly that triple - and this endpoint returns raw hits with no
// cross-circle collapse, unlike the suggestions endpoint which sums counts inside a
// terms aggregation. So a circle whose vector-store option lists related circles
// gets the same value back once per circle. Merge here: hits arrive sorted by
// _score then count, so first-seen wins the ordering and the counts add up.
export const mergeByValue = (hits?: EntityValueSearchHit[]): FilterOption[] => {
    const byValue = new Map<string, FilterOption>();

    for (const hit of hits || []) {
        const value = String(hit.value);
        const merged = byValue.get(value);

        if (merged) {
            merged.count += hit.count || 0;
            continue;
        }

        byValue.set(value, {
            label: hit.label || value,
            value,
            count: hit.count || 0,
            displayLabel: hit.displayLabel || value,
        });
    }

    return Array.from(byValue.values());
};

export const useEntityValueSearch = ({
    circleId,
    field,
    query,
    limit = DEFAULT_LIMIT,
}: {
    circleId: string;
    // One of the entity_values field keys (authors, persons, organizations,
    // locations, ...). Empty string disables the hook, so callers can stay
    // unconditional.
    field: string;
    query: string;
    limit?: number;
}): { results: FilterOption[]; loading: boolean } => {
    const api = useApi();
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    // A single character through bool_prefix + fuzziness:AUTO returns a page of
    // low-precision hits, so don't spend the request on it.
    const enabled = Boolean(circleId) && Boolean(field) && debouncedQuery.length >= MIN_QUERY_LENGTH;

    // The fetcher reads its arguments off the key rather than the closure, so a
    // response can never be attributed to a query it wasn't made for.
    const { data, isLoading } = useSWR(
        enabled ? ['entity-values-search', circleId, field, debouncedQuery, limit] : null,
        async ([, searchCircleId, searchField, searchQuery, searchLimit]) => {
            // circleId must be a single string: the endpoint does
            // [circleId, ...relatedCircles] with no normalization, so an array would
            // nest and break the Elasticsearch terms clause. It expands to the
            // vector-store circles itself, matching the suggestions call's coverage.
            const response = await api.post({
                path: 'groups/elastic/entity-values/search',
                body: {
                    circleId: searchCircleId,
                    query: searchQuery,
                    fields: [searchField],
                    limit: searchLimit,
                },
            });
            return mergeByValue(response?.results);
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            // An already-typed section stays mounted behind the accordion, and the
            // app-level onErrorRetry backs off 5/10/20s - without this, one Elastic
            // outage queues a retry storm per keystroke.
            shouldRetryOnError: false,
        }
    );

    return { results: data || EMPTY, loading: isLoading };
};
